import React from 'react';
import { connect } from 'react-redux';
import { Text, Input, Checkbox, MessageBarBody } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import * as ComponentFunctions from './PractitionerConsumptionRateDrawer.Functions';
import { CurrencySelectorComponent } from '../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import { formatCurrencyAmount } from '../PractitionerConsumptions/PractitionerConsumptionCurrency';
import { usePractitionerConsumptionRateDrawerStyles } from './PractitionerConsumptionRateDrawer.Styles';
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

const PractitionerConsumptionRateDrawerComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = usePractitionerConsumptionRateDrawerStyles();
  const [newRate, setNewRate] = React.useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = React.useState<string>('USD');
  const [hasCurrencyBeenChosen, setHasCurrencyBeenChosen] = React.useState(false);
  const [applyToSettlement, setApplyToSettlement] = React.useState(false);

  const currentRate = props.Consumption?.rate;
  const hasRelatedSettlement = ComponentFunctions.canApplyExpandedScope(props.Consumption);

  React.useEffect(() => {
    setNewRate(currentRate !== undefined && currentRate !== null ? String(currentRate) : '');
    setSelectedCurrency(props.Consumption?.currencyCode || props.Consumption?.originalCurrencyCode || 'USD');
    setHasCurrencyBeenChosen(false);
    setApplyToSettlement(false);
  }, [props.Consumption?.id, currentRate, props.isOpen]);

  const hasSameCurrency = !!selectedCurrency && selectedCurrency === props.Consumption?.originalCurrencyCode;

  React.useEffect(() => {
    if (hasSameCurrency) {
      setNewRate('1');
    }
  }, [hasSameCurrency]);

  React.useEffect(() => {
    if (props.Consumption?.rateChanged && !props.isSaving && props.isOpen) {
      props.onClose();
    }
  }, [props.Consumption?.rateChanged, props.isSaving, props.isOpen, props.onClose]);

  const normalizedRate = hasSameCurrency ? 1 : ComponentFunctions.normalizeRateInput(newRate);
  const hasSelectedCurrency = !!selectedCurrency;
  const hasInvalidRate = newRate !== '' && normalizedRate === undefined && !hasSameCurrency;
  const canSave = hasSelectedCurrency && hasCurrencyBeenChosen && normalizedRate !== undefined && !props.isSaving;

  const currentUnitAmount = props.Consumption?.unitAmount;
  const currentCurrencyCode = props.Consumption?.currencyCode;
  const originalUnitAmount = props.Consumption?.originalUnitAmount;
  const sourceAmountForPreview =
    originalUnitAmount !== undefined && originalUnitAmount !== null && originalUnitAmount > 0
      ? originalUnitAmount
      : props.Consumption?.unitAmount;

  const previewAmount = sourceAmountForPreview !== undefined && normalizedRate !== undefined
    ? sourceAmountForPreview * normalizedRate
    : undefined;

  const onConfirm = () => {
    if (!props.Consumption || !canSave) return;

    props.ChangeRate(
      props.PractitionerId,
      props.ConsumptionId,
      normalizedRate!,
      applyToSettlement,
      applyToSettlement ? props.Consumption.relatedSettlementId : undefined,
      selectedCurrency,
    );
  };

  const contextLine = [props.Consumption?.protocolName, props.Consumption?.serviceName].filter(Boolean).join(' · ');

  return (
    <GuideDrawer
      open={props.isOpen}
      size='small'
      eyebrow={contextLine || undefined}
      title={t('consumptionRateWorkflow.drawerTitle')}
      onDismiss={props.onClose}
      testId='practitioner-consumption-rate-drawer'
      secondaryAction={{ label: t('common.cancel'), disabled: props.isSaving }}
      primaryAction={{
        label: t('consumptionRateWorkflow.confirm'),
        onClick: onConfirm,
        disabled: !canSave,
        loading: props.isSaving,
      }}
    >
      {/* ── Zona 1: Valores actuales (solo lectura) ── */}
      <div className={styles.infoCard}>
        <Text className={styles.sectionTitle}>{t('consumptionRateWorkflow.currentValue')}</Text>
        <div className={styles.dataGrid}>
          <div className={styles.dataItem}>
            <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.currentRate')}</Text>
            <Text className={styles.dataValue}>{props.Consumption?.rate ?? '-'}</Text>
          </div>
          <div className={styles.dataItem}>
            <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.rateType')}</Text>
            <Text className={styles.dataValue}>{ComponentFunctions.getRateTypeLabel(props.Consumption?.rateType)}</Text>
          </div>
          <div className={styles.dataItem}>
            <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.currentAmountInCurrency')}</Text>
            <Text className={styles.dataValue}>{formatCurrencyAmount(currentUnitAmount, currentCurrencyCode)}</Text>
          </div>
        </div>
        {hasRelatedSettlement && (
          <Text className={styles.settlementMeta}>
            {t('consumptionRateWorkflow.relatedSettlement')}: {props.Consumption?.relatedSettlementName || props.Consumption?.relatedSettlementId}
          </Text>
        )}
      </div>

      {/* ── Zona 2: Edición — moneda primero, luego cotización ── */}
      <GuideDrawerSection title={t('consumptionRateWorkflow.drawerTitle')}>
        <CurrencySelectorComponent
          label={t('consumptionRateWorkflow.currencyCode')}
          isRequired={false}
          errorMessage={undefined}
          defaultSelected={selectedCurrency}
          onCurrencyChange={(currency: string) => {
            setSelectedCurrency(currency);
            setHasCurrencyBeenChosen(true);
          }}
        />

        <GuideField
          label={t('consumptionRateWorkflow.newRate')}
          error={hasInvalidRate ? t('consumptionRateWorkflow.validation.invalidRate') : undefined}
        >
          <Input
            type='number'
            step={0.000001}
            value={newRate}
            disabled={!hasCurrencyBeenChosen || hasSameCurrency}
            onChange={(_, data) => setNewRate(data.value)}
          />
        </GuideField>

        {hasSameCurrency && (
          <Text className={styles.sameCurrencyBadge}>{t('consumptionRateWorkflow.sameCurrencyFixedRate')}</Text>
        )}
      </GuideDrawerSection>

      {/* ── Zona 3: Resultado de la conversión ── */}
      <div className={styles.resultCard}>
        <Text className={styles.sectionTitle}>{t('consumptionRateWorkflow.previewAmountInSelectedCurrency')}</Text>
        <div className={styles.dataGrid}>
          <div className={styles.dataItem}>
            <Text className={styles.dataLabel}>{`${selectedCurrency || '-'}`}</Text>
            <Text className={styles.dataValueAccent}>
              {previewAmount !== undefined ? formatCurrencyAmount(previewAmount, selectedCurrency) : '-'}
            </Text>
          </div>
          <div className={styles.dataItem}>
            <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.newRateToApply')}</Text>
            <Text className={styles.dataValue}>{newRate || '-'}</Text>
          </div>
        </div>
      </div>

      {hasRelatedSettlement ? (
        <Checkbox
          label={t('consumptionRateWorkflow.applyToRelatedSettlement')}
          checked={applyToSettlement}
          onChange={(_, data) => setApplyToSettlement(!!data.checked)}
        />
      ) : (
        <GuideHelpText>{t('consumptionRateWorkflow.expandedUnavailable')}</GuideHelpText>
      )}

      {props.error && (
        <GuideMessageBar intent='error'>
          <MessageBarBody>{props.error}</MessageBarBody>
        </GuideMessageBar>
      )}
    </GuideDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps,
)(PractitionerConsumptionRateDrawerComponent as any);
